import { Grid, Paper, Theme } from "@mui/material";
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import { RequestIndicator } from "components/CallServer";
import WidgetDrawer from "components/WidgetDrawer";
import { deepMerge } from "lib/DeepMerge";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedItemDto } from "models/ApiModels";
import * as React from "react";
import ReactResizeDetector from "react-resize-detector";
import { RouteComponentProps } from "react-router";
import { FeedPostWindowMessages } from "store/feed/windowMessages";
import { SidebarPostWindowMessages } from "store/sidebar/windowMessages";
import CommentBox from "views/Publication/Components/CommentBox";
import { PublicationBlockedInfo } from "views/Publication/Components/PublicationBlockedInfo";
import { PublicationDeletedInfo } from "views/Publication/Components/PublicationDeletedInfo";
import { PublicationNoAccessInfo } from "views/Publication/Components/PublicationNoAccessInfo";
import { PublicationNoDataInfo } from "views/Publication/Components/PublicationNoDataInfo";
import { PublicationUserBlockedInfo } from "views/Publication/Components/PublicationUserBlockedInfo";
import WidgetRelations from "views/Publication/Widgets/WidgetRelations";
import WidgetStatResume from "views/Publication/Widgets/WidgetStatResume";
import PostCard from "views/Timeline/Components/CardFactory/PostCard";

/////////////////////////////////////////////////////////////////////////////////////

export function CheckPublicationDataIsOkToContinue(data: FeedItemDto) {
    if (data == null)
        return <PublicationNoDataInfo />;

    if (data.publication.deleted)
        return <PublicationDeletedInfo publication={data.publication} />;

    if (!data.permissions.hasAccess)
        return <PublicationNoAccessInfo publication={data.publication} />;

    if (data.publication.bannedByAuthor)
        return <PublicationUserBlockedInfo publication={data.publication} />;

    if (!data.permissions.canViewBody)
        return <PublicationBlockedInfo publication={data.publication} />;

    return null;
}

/////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({
    ...TimelineStyleClasses(theme),
    ...FeedStyleClasses(theme)
});

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    loading: boolean;
    updating: boolean;
    dialog: any;
    data: FeedItemDto;
}

interface IProps {
}

interface IRouteParams {
    id: string;
}

type Props = IProps & RouteComponentProps<IRouteParams> & WithStyles<typeof styles>;

/////////////////////////////////////////////////////////////////////////////////////

class PostViewer extends React.PureComponent<Props, IState> {

    private feedWindowMessages = new FeedPostWindowMessages(window.opener);
    private sidebarWindowMessages = new SidebarPostWindowMessages(window.opener);

    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            dialog: null,
            updating: false,
            loading: true
        };
    }

    ///
    private onLoad() {
        api.getFeedItemPublication(this.props.match.params.id, true)
            .then((data) => {
                this.setState({ data, loading: false });
            })
            .catch(() => {
                this.setState({ loading: false });
            });
    }

    ///
    private handleDelete = (publicationId: string): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            api.deletePublication(publicationId, PublicationType.Post)
                .then((result) => {
                    if (result) {
                        this.feedWindowMessages.tryDeleteFeedData(publicationId);
                        window.close();
                    }
                    resolve(result);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    ///
    private handleReload = () => {
        return api.getFeedItemPublication(this.state.data.publicationId, false)
            .then((newData) => {
                this.setState({ data: newData });
            })
            .catch(() => {
            });
    };

    ///
    private handleInfo = () => {
        this.setState({
            dialog: (
                <WidgetDrawer onClose={() => this.setState({ dialog: null })} >
                    <WidgetStatResume data={this.state.data} />
                </WidgetDrawer>
            )
        });
    };

    ///
    componentDidMount() {
        this.onLoad();
    }

    ///
    render() {
        const { classes } = this.props;
        const { data, loading } = this.state;

        if (loading)
            return <RequestIndicator type="request" />;

        var noContinueMessage = CheckPublicationDataIsOkToContinue(data);
        if (noContinueMessage != null) return <div className={classes.timelineContainer}>{noContinueMessage}</div>;

        return (
            <>
                <ReactResizeDetector handleWidth refreshMode="debounce" refreshRate={0}>
                    {
                        (d: ReactResizeDetectorDimensions) =>

                            <Grid container spacing={1} direction="row" style={{ flexWrap: "nowrap" }}>
                                <Grid item xs>

                                    <div className={classes.timelineContainer}>

                                        <PostCard
                                            key={data.publicationId}
                                            item={data}
                                            classes={classes}
                                            feedType={FeedType.Any}
                                            showRelationsButton={d.width < 920}
                                            onInfoRequest={d.width < 920 ? this.handleInfo : null}
                                            onRemovePublication={() => this.handleDelete(data.publicationId)}
                                            onReloadPublication={this.handleReload}
                                            onUpdateCard={
                                                (item: DeepPartial<FeedItemDto>) => {

                                                    this.setState({
                                                        data: deepMerge(this.state.data, item, { arrayMerge: (target: any, source: any, options: any) => source })
                                                    });

                                                    // Só atualiza feed principal com mudanças ocorridas na Interação.
                                                    this.feedWindowMessages.tryUpdateFeedData({
                                                        interaction: item.interaction
                                                    });
                                                }
                                            }
                                            onSidebarResetRequest={
                                                () => {
                                                    this.sidebarWindowMessages.reset();
                                                }
                                            }
                                        />

                                        <Paper>
                                            <CommentBox publicationId={data.publicationId} />
                                        </Paper>
                                    </div>

                                </Grid>

                                {
                                    d.width > 919 &&
                                    <Grid item style={{ width: 350 }}>
                                        {
                                            data.publication.hasRelations &&
                                            <WidgetRelations publicationId={data.publicationId} />
                                        }
                                        <WidgetStatResume data={data} />
                                    </Grid>
                                }
                            </Grid>
                    }
                </ReactResizeDetector>

                {this.state.dialog}
            </>
        );
    }
}

export default withStyles(styles)(PostViewer);